import { NextPage } from "next";
import { useEffect, useMemo } from "react";
import { useSetRecoilState } from "recoil";
import { Flights, Services, FlightsByDestination, NewsDetails, AirlineList, ModuleMatcher2 } from "@/components";
import { IModuleDTO, IPageDTO, PAGE_CAT } from "@/interfaces/IPageDTO";
import { CrumbsItemAtom, HideCrumbsAtom } from "@/atoms";
import { getAllPages, getFooterData, getNavigationData } from "@/api";
import { useTranslation } from 'react-i18next';
import { AIRLINES_LIST_PAGE_URLS } from "@/config/airlinesConfig";
import { FLIGHT_DETAIL_PAGE_URLS, FLIGHT_LIST_PAGE_URLS, MovementType } from "@/config/flightPages";

import Head from "next/head";
import PagePathAtom from "@/atoms/PagePathAtom";
import getTargetUrl from "@/api/getTargetUrl";
import Script from "next/script";

export async function getServerSideProps(ctx: any) {

  const { lang, paths } = ctx.params;
  
  const data = await getAllPages({ lang: lang, paths: paths });

  const footerData = await getFooterData();
  const navigationData = await getNavigationData();

  ctx.res.setHeader('Cache-Control', 'public, max-age=120');

  //NOTE: MODULEMATCHER SERVER SIDE PREFETCH
  const dataPassedToModuleMatcher = data.data?.find((item: { xtaxAirports: { ti: any[]; }; }) => 
    item.xtaxAirports.ti.find(airportItem => airportItem.label.toLowerCase() === process.env.NEXT_PUBLIC_AIRPORT_REFERENCE?.toLowerCase()));

  const excludes: string[] = [];

  if (dataPassedToModuleMatcher) {
    const dataPassToModuleMatcher = await Promise.all(dataPassedToModuleMatcher!.lstModule?.filter((item: { xconModules: { linkedcontenttype: any; }; }) => 
      !excludes?.includes(item.xconModules.linkedcontenttype)).map( async (moduleItem: { xconModules: { linkedcontenttype: any; targeturl: string; }; id: any; }) => {

      let moduleData: any;
  
      switch (moduleItem.xconModules.linkedcontenttype) {
        case "m01":
          moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
          break;
        case "m02":
          moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
          break;
        case "m03":
          moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
          break;
        case "m04":
          moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
          break;
        case "m05":
          moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
          break;
        case "m06":
          moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
          //TODO: fetch delle card?
          break;
        case "m07":
          moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
          break;
        case "m08":
          moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
          //TODO: fetch delle card?
          break;
        case "m09":
          moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
          break;
        case "m10":
          moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
          break;
        case "m11":
          moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
          break;
        case "m12":
          moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
          break;
        case "m13":
          moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
          break;
        case "m14":
          moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
          break;
        case "m15":
          moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
          //TODO: check fetch dentro questo modulo
          break;
        case "m16":
          moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
          //TODO: fetch delle card?
          break;
        case "m17":
          moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
          break;
        case "m18":
          moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
          break;
        case "m19":
          moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
          break;
        case "m20":
          moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
          break;
        default:
          moduleData = null;
      }
  
      return {
        linkedcontenttype: moduleItem.xconModules.linkedcontenttype,
        id: moduleItem.id,
        moduleData: moduleData
      };
  
    }));

    return {
      props: {
        lang,
        data,
        footerData,
        navigationData,
        paths,
        dataPassToModuleMatcher
      },
    };
  } else {
    return {
      notFound: true
    }
  }
}

interface IPageProps {
  data: { data: IPageDTO[] };
  paths: string[];
  lang: string;
  navigationData: any;
  dataPassToModuleMatcher: any;
}

const PageComponent: NextPage<IPageProps> = ({ data, paths, lang, navigationData, dataPassToModuleMatcher }) => {

  const pageData = data?.data?.find(item => 
    item.xtaxAirports.ti.find(airportItem => 
      airportItem.label.toLowerCase() === process.env.NEXT_PUBLIC_AIRPORT_REFERENCE?.toLowerCase()));

  const pagePath = pageData?.xtaxNavigationMenu?.ti?.[0]?.path || ''

  const { i18n } = useTranslation();
  
  const moduleToExcludes = useMemo(() => {
    let excludedModules: string[] = [];
    if (pageData && pageData.enuPageCategory === PAGE_CAT.Voli && 
        !(FLIGHT_DETAIL_PAGE_URLS[MovementType.D][lang] === pageData.lblUrlPath[lang] || 
          FLIGHT_DETAIL_PAGE_URLS[MovementType.A][lang] === pageData.lblUrlPath[lang])) {
      excludedModules = ['m05'];
    }
    if (pageData && pageData.enuPageCategory === PAGE_CAT.Destinazione) {
      excludedModules = ['m10', "m07", "m05"];
    }
    if (pageData && pageData.enuPageCategory === PAGE_CAT.Servizi) {
      excludedModules = ['m05', "m07", "m10"];
    }
    if (pageData && pageData.lblUrlPath[lang].toLowerCase() === AIRLINES_LIST_PAGE_URLS[lang].toLowerCase()) {
      excludedModules = ['m10', 'm05'];
    }
    return excludedModules
  }, [pageData, lang])

  const setPagePath = useSetRecoilState(PagePathAtom);
  const setCrumbsItem = useSetRecoilState(CrumbsItemAtom);
  const setHideCrumbs = useSetRecoilState(HideCrumbsAtom);

  useEffect(() => {
    setPagePath(pagePath)
    setCrumbsItem(navigationData)
    setHideCrumbs(pageData?.flgHideNavigationéath ? pageData?.flgHideNavigationéath : false)
  }, [pageData, lang, pagePath, navigationData])

  useEffect(() =>  {
    i18n.changeLanguage(lang?.toLowerCase())
  }, [lang, i18n])

  // Funzione utility per generare tutti i JSON-LD necessari
  const generateStructuredData = (pageData: any, lang: string, siteUrl: string, navigationData: any) => {
    
    // 1. WebSite Schema
    const webSiteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": process.env.NEXT_PUBLIC_IATA_AIRPORT_REFERENCE === 'mxp' ? "Milano Malpensa Airport" : "Milano Linate Airport",
      "url": `${siteUrl}/${lang}`,
      "potentialAction": {
        "@type": "SearchAction",
        "target": `${siteUrl}/${lang}/search?query={search_term_string}`,
        "query-input": "required name=search_term_string"
      }
    };

    // 2. Organization Schema  
    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": process.env.NEXT_PUBLIC_IATA_AIRPORT_REFERENCE === 'mxp' ? "Milano Malpensa - Aeroporti di Milano" : "Milano Linate - Aeroporti di Milano",
      "url": `${siteUrl}/${lang}`,
      // Aggiungi questi campi quando hai i dati reali:
      // "logo": `${siteUrl}/path-to-your-logo.png`,
      // "contactPoint": [
      //   {
      //     "@type": "ContactPoint", 
      //     "telephone": "+39-02-XXXXXX",
      //     "contactType": "customer service"
      //   }
      // ],
      // "sameAs": [
      //   "https://www.linkedin.com/company/your-linkedin",
      //   "https://twitter.com/your-twitter"
      // ]
    };

    // 3. WebPage Schema (quello esistente migliorato)
    const webPageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": pageData?.lblSeoTitle[lang],
      "description": pageData?.lblSeoDescription[lang],
      "url": `${siteUrl}${pageData?.lblUrlPath[lang]}`
    };

    // 4. BreadcrumbList Schema (solo se ci sono breadcrumb)
    let breadcrumbSchema = null;
    if (navigationData && navigationData.length > 0) {
      breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": navigationData.map((item: any, index: number) => ({
          "@type": "ListItem",
          "position": index + 1,
          "item": {
            "@id": `${siteUrl}${item.url}`,
            "name": item.name
          }
        }))
      };
    }

    return {
      webSiteSchema,
      organizationSchema, 
      webPageSchema,
      breadcrumbSchema
    };
  };

  const siteMxp = process.env.NEXT_PUBLIC_SITE_MALPENSA;
  const siteLin = process.env.NEXT_PUBLIC_SITE_LINATE;
  const isMpx = process.env.NEXT_PUBLIC_IATA_AIRPORT_REFERENCE;

  const siteUrl = (isMpx === 'mxp' ? siteMxp : siteLin) || '';

  return (
    <>
      <Head>
        <title>{pageData?.lblSeoTitle?.[lang] || 'Milano Airport'}</title>
        <meta name="title" content={pageData?.lblSeoTitle?.[lang] || 'Milano Airport'}></meta>
        <meta name="description" content={pageData?.lblSeoDescription?.[lang] || ''}></meta>
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png"/>
        <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png"/>
        
        {/* Canonical URL */}
        <link rel="canonical" href={`${siteUrl}${pageData?.lblUrlPath?.[lang] || ''}`} />
        
        {/* Hreflang tags */}
        {pageData?.lblUrlPath && (
          <>
            <link rel="alternate" hrefLang="it" href={`${siteUrl}${pageData.lblUrlPath.it || pageData.lblUrlPath['it'] || ''}`} />
            <link rel="alternate" hrefLang="en" href={`${siteUrl}${pageData.lblUrlPath.en || pageData.lblUrlPath['en'] || ''}`} />
          </>
        )}
        
        <script src="https://static.cdn-apple.com/businesschat/start-chat-button/2/index.js"></script>

        {process.env.NEXT_PUBLIC_IATA_AIRPORT_REFERENCE === 'mxp' ? 
          pageData && pageData.enuPageCategory === PAGE_CAT.Voli ? 
            <>
              <script type="application/javascript" src="https://ced.sascdn.com/tag/2661/smart.js" async></script>
              <script type="application/javascript">
                {`
                  var sas = sas || {};
                  sas.cmd = sas.cmd || [];
                  sas.cmd.push(function() {
                      sas.setup({ networkid: 2661, domain: 'https://www14.smartadserver.com', async: true });
                  });
                  sas.cmd.push(function() {
                      sas.call("onecall", {
                          siteId: 299227,
                          pageId: 1095954,
                          formats: [
                              { id: 56461 },
                              { id: 56463 }
                          ],
                          target: 'lang=${lang}'
                      });
                  });
                `}
              </script>
            </> 
          : 
          <>
            <script type="application/javascript" src="https://ced.sascdn.com/tag/2661/smart.js" async></script>
            <script>{
              ` var sas = sas || {};
                sas.cmd = sas.cmd || [];
                sas.cmd.push(function() {
                    sas.setup({ networkid: 2661, domain: 'https://www14.smartadserver.com', async: true });
                });
                sas.cmd.push(function() {
                    sas.call("onecall", {
                        siteId: 299227,
                        pageId: 1095952,
                        formats: [
                            { id: 56461 },
                            { id: 56463 }
                        ],
                        target: 'lang=${lang}'
                    });
                });
              `}
            </script>
          </> 
        : 
          pageData && pageData.enuPageCategory === PAGE_CAT.Voli ? 
          <>
              <script type="application/javascript" src="https://ced.sascdn.com/tag/2661/smart.js" async></script>
              <script>{
                ` var sas = sas || {};
                  sas.cmd = sas.cmd || [];
                  sas.cmd.push(function() {
                      sas.setup({ networkid: 2661, domain: 'https://www14.smartadserver.com', async: true });
                  });
                  sas.cmd.push(function() {
                      sas.call("onecall", {
                          siteId: 299226,
                          pageId: 1095951,
                          formats: [
                              { id: 56461 },
                              { id: 56463 }
                          ],
                          target: 'lang=${lang}'
                      });
                  });
                `}
              </script>
            </>
          : 
          <>
            <script type="application/javascript" src="https://ced.sascdn.com/tag/2661/smart.js" async></script>
            <script>{
              ` var sas = sas || {};
                sas.cmd = sas.cmd || [];
                sas.cmd.push(function() {
                    sas.setup({ networkid: 2661, domain: 'https://www14.smartadserver.com', async: true });
                });
                sas.cmd.push(function() {
                    sas.call("onecall", {
                        siteId: 299226,
                        pageId: 1095949,
                        formats: [
                            { id: 56461 },
                            { id: 56463 }
                        ],
                        target: 'lang=${lang}'
                    });
                });
              `}
            </script>
          </>
        }

        {/* JSON-LD Structured Data - Multiple schemas */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify([
              generateStructuredData(pageData, lang, siteUrl, navigationData).webSiteSchema,
              generateStructuredData(pageData, lang, siteUrl, navigationData).organizationSchema,
              generateStructuredData(pageData, lang, siteUrl, navigationData).webPageSchema,
              ...(generateStructuredData(pageData, lang, siteUrl, navigationData).breadcrumbSchema 
                  ? [generateStructuredData(pageData, lang, siteUrl, navigationData).breadcrumbSchema] 
                  : [])
            ])
          }}
        />
      </Head>

      <main>

        {process.env.NEXT_PUBLIC_IATA_AIRPORT_REFERENCE === 'mxp' ? 
          pageData && pageData.enuPageCategory === PAGE_CAT.Voli ?
            <>
              <div id="sas_56461"></div>
              <Script type="application/javascript">{`
                  var sas = sas || {};
                  sas.cmd = sas.cmd || [];
                  sas.cmd.push(function() {
                  sas.render("56461");  // Formato: 320x50_leaderboard_mobile
                });`}
              </Script>

              <div id="sas_56463"></div>
              <Script type="application/javascript">{`
                  var sas = sas || {};
                  sas.cmd = sas.cmd || [];
                  sas.cmd.push(function() {
                  sas.render("56463");  // Formato: 970x90_masthead
                });`}
              </Script>
            </> 
          : 
          <>
              <div id="sas_56461"></div>
              <Script type="application/javascript">{`
                  var sas = sas || {};
                  sas.cmd = sas.cmd || [];
                  sas.cmd.push(function() {
                  sas.render("56461");  // Formato: 320x50_leaderboard_mobile
                });`}
              </Script>

              <div id="sas_56463"></div>
              <Script type="application/javascript">{`
                  var sas = sas || {};
                  sas.cmd = sas.cmd || [];
                  sas.cmd.push(function() {
                  sas.render("56463");  // Formato: 970x90_masthead
                });`}
              </Script>
            </> 
          :
          pageData && pageData.enuPageCategory === PAGE_CAT.Voli ? 
            <>
              <div id="sas_56461"></div>
              <Script type="application/javascript">{`
                  var sas = sas || {};
                  sas.cmd = sas.cmd || [];
                  sas.cmd.push(function() {
                  sas.render("56461");  // Formato: 320x50_leaderboard_mobile
                });`}
              </Script>

              <div id="sas_56463"></div>
              <Script type="application/javascript">{`
                  var sas = sas || {};
                  sas.cmd = sas.cmd || [];
                  sas.cmd.push(function() {
                  sas.render("56463");  // Formato: 970x90_masthead
                });`}
              </Script>
            </> 
          : 
            <>
              <div id="sas_56461"></div>
              <Script type="application/javascript">{`
                  var sas = sas || {};
                  sas.cmd = sas.cmd || [];
                  sas.cmd.push(function() {
                  sas.render("56461");  // Formato: 320x50_leaderboard_mobile
                });`}
              </Script>

              <div id="sas_56463"></div>
              <Script type="application/javascript">{`
                  var sas = sas || {};
                  sas.cmd = sas.cmd || [];
                  sas.cmd.push(function() {
                  sas.render("56463");  // Formato: 970x90_masthead
                });`}
              </Script>
            </> 
          }

        {pageData?.metainfo?.name.toLowerCase() === 'news' ? <NewsDetails language={lang} data={pageData} /> : null}

        <ModuleMatcher2 lang={lang} excludes={moduleToExcludes} moduleData={dataPassToModuleMatcher} />

        {pageData && pageData.enuPageCategory === PAGE_CAT.Voli && (FLIGHT_LIST_PAGE_URLS[MovementType.D][lang] === pageData.lblUrlPath[lang] || FLIGHT_LIST_PAGE_URLS[MovementType.A][lang] === pageData.lblUrlPath[lang]) ? <Flights language={lang} dividerTargetUrl={pageData?.lstModule.find(item => item.xconModules.linkedcontenttype === 'm05')?.xconModules.targeturl || ''} /> : null}
        
        {pageData && pageData.enuPageCategory === PAGE_CAT.Servizi  ?
          (<Services language={lang} >
            <ModuleMatcher2 lang={lang} excludes={['m18', 'm11']} moduleData={dataPassToModuleMatcher} />
          </Services>
          ) : null}
        
        {pageData && pageData.enuPageCategory === PAGE_CAT.Destinazione ?
          (<FlightsByDestination language={lang} dividerTargetUrl={pageData?.lstModule.find(item => item.xconModules.linkedcontenttype === 'm05')?.xconModules.targeturl || ''} >
            <ModuleMatcher2 lang={lang} excludes={['m18', 'm05', 'm11']} moduleData={dataPassToModuleMatcher} />
          </FlightsByDestination>
          ) : null}
        
        {pageData && pageData.lblUrlPath[lang].toLowerCase() === AIRLINES_LIST_PAGE_URLS[lang].toLowerCase() ? <AirlineList language={lang} dividerTargetUrl={pageData?.lstModule.find(item => item.xconModules.linkedcontenttype === 'm05')?.xconModules.targeturl || ''} ><ModuleMatcher2 lang={lang} excludes={['m18', 'm05']} moduleData={dataPassToModuleMatcher} /></AirlineList> : null}
      </main>
    </>
  )
};

export default PageComponent;